import { type ReactNode } from 'react'
import { Head, Link } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Pencil, Plus } from 'lucide-react'
import type { ResearchEventSummary, ResearchOrganizationDetail } from '@/types/research'

type Props = {
  organization: ResearchOrganizationDetail & { archived: boolean }
  events: ResearchEventSummary[]
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function ResearchOrganizationShow({ organization, events }: Props) {
  return (
    <>
      <Head title={`Research - ${organization.name}`} />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold tracking-tight">{organization.name}</h2>
            {organization.archived && <Badge variant="destructive">Archived</Badge>}
          </div>
          <Button asChild variant="outline">
            <Link href={`/admin/research/organizations/${organization.id}/edit`}>
              <Pencil className="mr-2 size-4" />
              Edit
            </Link>
          </Button>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <p className="text-muted-foreground">Location</p>
                <p className="font-medium">
                  {[organization.city, organization.state].filter(Boolean).join(', ') || '—'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Budget</p>
                <p className="font-medium">{organization.budget || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Website</p>
                {organization.website ? (
                  <a
                    href={organization.website}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary hover:underline"
                  >
                    {organization.website}
                  </a>
                ) : (
                  <p className="font-medium">—</p>
                )}
              </div>
              {organization.causes.length > 0 && (
                <div>
                  <p className="text-muted-foreground">Causes</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {organization.causes.map(cause => (
                      <Badge key={cause} variant="secondary">{cause}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {organization.about && (
                <div>
                  <p className="text-muted-foreground">About</p>
                  <p>{organization.about}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="col-span-2">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold tracking-tight">Events</h3>
              <Button asChild size="sm">
                <Link href={`/admin/research/events/new?organization_id=${organization.id}`}>
                  <Plus className="mr-2 size-4" />
                  New Event
                </Link>
              </Button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map(event => (
                    <TableRow key={event.id}>
                      <TableCell className="font-medium">
                        <Link href={`/admin/research/events/${event.id}`} className="hover:underline">
                          {event.name || 'Untitled'}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{event.event_type || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(event.date)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {[event.city, event.state].filter(Boolean).join(', ') || '—'}
                      </TableCell>
                      <TableCell>
                        {event.status ? <Badge variant="secondary">{event.status}</Badge> : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="ghost" size="sm">
                          <Link href={`/admin/research/events/${event.id}/edit`}>Edit</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {events.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                        No events recorded for this organization yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

ResearchOrganizationShow.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
